import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ClipboardText, FilePdf, Lightning, Warning } from "@phosphor-icons/react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { formatDate, scoreBandLabel } from "../lib/utils-extra";

const STATUS_COLOR = {
  draft: "bg-yellow-50 text-[#D97706] border-yellow-200",
  submitted: "bg-blue-50 text-[#0284C7] border-blue-200",
  finalized: "bg-green-50 text-[#16A34A] border-green-200",
};

export default function Evaluations() {
  const { user } = useAuth();
  const canManage = ["owner", "hr", "manager"].includes(user?.role);
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [empCount, setEmpCount] = useState(null);
  const [tplCount, setTplCount] = useState(null);

  const reload = useCallback(async () => {
    const q = filter === "all" ? {} : { params: { status: filter } };
    const { data } = await api.get("/evaluations", q);
    setList(data);
  }, [filter]);
  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    // Dependency cues are only relevant to roles that can create evaluations.
    if (!canManage) return;
    api.get("/employees").then(({ data }) => setEmpCount(data.length)).catch(() => setEmpCount(0));
    api.get("/kpi-templates").then(({ data }) => setTplCount(data.length)).catch(() => setTplCount(0));
  }, [canManage]);

  const noEmployees = empCount === 0;
  const noTemplates = tplCount === 0;
  const blocked = canManage && (noEmployees || noTemplates);
  const blockMsg = noEmployees && noTemplates
    ? "You need at least one employee and one KPI template before creating evaluations."
    : noEmployees
      ? "You need at least one employee before creating evaluations."
      : "You need at least one KPI template before creating evaluations.";

  const downloadPdf = async (id) => {
    const res = await api.get(`/evaluations/${id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `evaluation_${id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="evaluations-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="small-label">Performance</div>
          <h1 className="font-display text-3xl font-semibold mt-1">Evaluations</h1>
          <p className="text-[#475569] text-sm mt-1">All staff reviews in one place.</p>
        </div>
        <div className="flex gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="finalized">Finalized</SelectItem>
            </SelectContent>
          </Select>
          {canManage && (blocked ? (
            <Button variant="outline" disabled data-testid="start-cycle-button">
              <Lightning size={16} className="mr-1" /> Start Cycle
            </Button>
          ) : (
            <Link to="/evaluations/cycles/new" data-testid="start-cycle-button">
              <Button variant="outline"><Lightning size={16} className="mr-1" /> Start Cycle</Button>
            </Link>
          ))}
          {canManage && (blocked ? (
            <Button className="bg-[#0EA5E9] hover:bg-[#0284C7]" disabled data-testid="new-evaluation-button">
              <Plus size={18} className="mr-1" /> New
            </Button>
          ) : (
            <Link to="/evaluations/new" data-testid="new-evaluation-button">
              <Button className="bg-[#0EA5E9] hover:bg-[#0284C7]"><Plus size={18} className="mr-1" /> New</Button>
            </Link>
          ))}
        </div>
      </div>

      {blocked && empCount !== null && tplCount !== null && (
        <Card className="border-[#D97706]/30 bg-[#D97706]/5" data-testid="evaluations-dependency-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <Warning size={20} weight="duotone" color="#D97706" className="mt-0.5 shrink-0" />
            <div className="flex-1 text-sm">
              <div className="font-medium text-[#0F172A]">Almost ready</div>
              <p className="text-[#475569] mt-0.5">{blockMsg}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              {noEmployees && (
                <Link to="/employees"><Button size="sm" variant="outline" data-testid="goto-employees-button">Add Employee</Button></Link>
              )}
              {noTemplates && (
                <Link to="/templates"><Button size="sm" variant="outline" data-testid="goto-templates-button">Add Template</Button></Link>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {list.length === 0 && (
          <Card className="surface-card border-[#E2E8F0] md:col-span-2">
            <CardContent className="p-10 text-center">
              <ClipboardText size={36} className="mx-auto text-[#475569]" weight="duotone" />
              <div className="mt-3 font-display text-lg font-semibold">No evaluations yet</div>
              <p className="text-sm text-[#475569] mt-1">
                {blocked ? blockMsg : canManage ? "Start by creating your first review." : "You have no evaluations yet."}
              </p>
              {!blocked && canManage && (
                <Link to="/evaluations/new" className="inline-block mt-4">
                  <Button className="bg-[#0EA5E9] hover:bg-[#0284C7]">Start an evaluation</Button>
                </Link>
              )}
            </CardContent>
          </Card>
        )}
        {list.map((e) => (
          <Card key={e.id} className="surface-card border-[#E2E8F0]" data-testid={`evaluation-card-${e.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-display text-lg font-semibold">{e.employee_name}</div>
                  <div className="text-xs text-[#475569] mt-1">{e.position_name} · {e.department}</div>
                  <div className="text-xs text-[#475569] mt-1">{formatDate(e.period_start)} → {formatDate(e.period_end)}</div>
                </div>
                <Badge className={`${STATUS_COLOR[e.status]} capitalize`}>{e.status}</Badge>
              </div>
              {e.final_score != null && (
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-3xl font-semibold">{e.final_score.toFixed(1)}<span className="text-base text-[#475569]">%</span></span>
                  <span className="text-xs text-[#475569]">{e.interpretation || scoreBandLabel(e.final_score)}</span>
                </div>
              )}
              <div className="flex gap-2 mt-4">
                <Link to={`/evaluations/${e.id}`}>
                  <Button variant="outline" size="sm" data-testid={`open-evaluation-${e.id}`}>Open</Button>
                </Link>
                {e.status === "finalized" && (
                  <Button variant="ghost" size="sm" onClick={() => downloadPdf(e.id)} data-testid={`pdf-${e.id}`}>
                    <FilePdf size={14} className="mr-1" /> PDF
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
